import React, { useState, useEffect } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import chatService from '@/services/chatService';
import './ChatWindow.css';

const ChatWindow = ({ onClose, isWidget = false }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [agentState, setAgentState] = useState('listening'); // Track agent state
  const [humanAgentPresent, setHumanAgentPresent] = useState(false); // Track human agent presence

  // Handler to start a new session
  const handleNewSession = async () => {
    console.log('🆕 [ChatWindow] User requested new session');
    setMessages([]); // Clear UI immediately
    setIsLoading(false);
    setIsConnecting(true);
    
    try {
      const result = await chatService.startNewSession();
      if (result.success) {
        console.log('✅ [ChatWindow] New session started successfully');
      }
    } catch (error) {
      console.error('❌ [ChatWindow] Failed to start new session:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  // Initialize LiveKit connection on mount
  useEffect(() => {
    let unsubscribeMessages = null;
    let unsubscribeSessionReset = null;
    let unsubscribeAgentState = null;
    let unsubscribeHumanAgent = null;
    let isMounted = true;

    const initializeChat = async () => {
      try {
        setIsConnecting(true);
        
        // IMPORTANT: Register message listener BEFORE connecting
        // This ensures we capture messages that arrive during connection (e.g., greeting)
        unsubscribeMessages = chatService.onMessage((message) => {
          console.log('🎯 [ChatWindow] Received message in component:', message);
          console.log('🎯 [ChatWindow] Component mounted:', isMounted);
          if (isMounted) {
            console.log('🎯 [ChatWindow] Updating messages state');
            setMessages(prev => {
              console.log('🎯 [ChatWindow] Previous messages:', prev.length);
              const newMessages = [...prev, message];
              console.log('🎯 [ChatWindow] New messages:', newMessages.length);
              return newMessages;
            });
            // Don't set isLoading to false here - let agent state handle it
          } else {
            console.warn('⚠️ [ChatWindow] Component not mounted, skipping state update');
          }
        });
        console.log('✅ Message listener registered BEFORE connect');
        
        // Listen for session reset events (new LiveKit session)
        unsubscribeSessionReset = chatService.onSessionReset((sessionInfo) => {
          console.log('🆕 [ChatWindow] New session started, clearing messages:', sessionInfo);
          if (isMounted) {
            setMessages([]); // Clear all messages for new session
            setIsLoading(false);
            setAgentState('listening'); // Reset agent state
            console.log('✅ [ChatWindow] Messages cleared for new session');
          }
        });
        
        // Listen for agent state changes
        unsubscribeAgentState = chatService.onAgentStateChange((state) => {
          console.log('🤖 [ChatWindow] Agent state changed to:', state);
          if (isMounted) {
            setAgentState(state);
            // When agent finishes speaking/thinking, stop loading
            if (state === 'listening') {
              setIsLoading(false);
            }
          }
        });
        
        // Listen for human agent events (join, message, left)
        unsubscribeHumanAgent = chatService.onHumanAgentEvent((event) => {
          console.log('🧑‍💼 [ChatWindow] Human agent event:', event);
          if (isMounted) {
            if (event.type === 'joined') {
              // Update human agent presence state
              setHumanAgentPresent(true);
              
              // Add a system message indicating human agent has joined
              setMessages(prev => [...prev, {
                id: event.data.id || Date.now(),
                text: 'تم انضمام موظف الدعم', // "Human agent has joined" in Arabic
                sender: 'system-label',
                labelType: 'human-agent-joined',
                timestamp: event.data.timestamp || new Date().toISOString(),
                participant: event.data.identity,
              }]);
              setIsLoading(false);
            } else if (event.type === 'left') {
              // Update human agent presence state
              setHumanAgentPresent(false);
              
              // Add a system message indicating human agent has left
              setMessages(prev => [...prev, {
                id: event.data.id || Date.now(),
                text: 'غادر موظف الدعم المحادثة', // "Human agent has left" in Arabic
                sender: 'system-label',
                labelType: 'human-agent-left',
                timestamp: event.data.timestamp || new Date().toISOString(),
                participant: event.data.identity,
              }]);
            }
            // Note: Human agent messages are delivered via onMessage, 
            // they're already handled and will show with sender: 'human-agent'
          }
        });
        
        // Initialize and connect to LiveKit
        const result = await chatService.connect();
        
        // Check if component is still mounted before updating state
        if (!isMounted) return;
        
        if (result.success) {
          console.log('✅ Chat service connected');
        } else {
          console.error('❌ Failed to connect to chat service');
          // Show error message
          setMessages(prev => [...prev, {
            id: Date.now(),
            text: 'عذراً، فشل الاتصال بخدمة الدردشة. يرجى المحاولة مرة أخرى.',
            sender: 'system',
            timestamp: new Date().toISOString(),
          }]);
        }
      } catch (error) {
        console.error('❌ Chat initialization error:', error);
      } finally {
        if (isMounted) {
          setIsConnecting(false);
        }
      }
    };

    initializeChat();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (unsubscribeMessages) {
        unsubscribeMessages();
      }
      if (unsubscribeSessionReset) {
        unsubscribeSessionReset();
      }
      if (unsubscribeAgentState) {
        unsubscribeAgentState();
      }
      if (unsubscribeHumanAgent) {
        unsubscribeHumanAgent();
      }
      // IMPORTANT: Don't disconnect here!
      // The connection should persist across component mounts/unmounts
      // Only disconnect when the user explicitly closes the app/tab
      console.log('🔄 ChatWindow unmounting, keeping connection alive');
    };
  }, []); // Empty dependency array - run once on mount

  const handleSendMessage = async (text) => {
    // Add user message to UI immediately
    const userMessage = {
      id: Date.now(),
      text,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Send message via LiveKit
      const result = await chatService.sendMessage(text);
      
      if (!result.success) {
        throw new Error('Failed to send message');
      }
      
      console.log('✅ Message sent successfully');
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      
      // Show error message
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: 'عذراً، فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.',
        sender: 'system',
        timestamp: new Date().toISOString(),
      }]);
      
      setIsLoading(false);
    }
  };

  // Handle sending files/images to human agent
  const handleSendFile = async (file) => {
    if (!humanAgentPresent) {
      console.warn('⚠️ Cannot send file: No human agent present');
      return;
    }

    // Create a preview URL for the file
    const fileUrl = URL.createObjectURL(file);
    const isImage = file.type.startsWith('image/');

    // Add file message to UI immediately with uploading status
    const fileMessage = {
      id: Date.now(),
      text: isImage ? '' : file.name,
      sender: 'user',
      timestamp: new Date().toISOString(),
      fileType: isImage ? 'image' : 'file',
      fileName: file.name,
      fileSize: file.size,
      fileMimeType: file.type,
      fileUrl: fileUrl,
      uploadStatus: 'uploading',
      uploadProgress: 0,
    };

    setMessages(prev => [...prev, fileMessage]);

    try {
      // Send file via LiveKit to human agent
      const result = await chatService.sendFileToHumanAgent(file, (progress) => {
        // Update upload progress
        setMessages(prev => prev.map(msg => 
          msg.id === fileMessage.id 
            ? { ...msg, uploadProgress: progress } 
            : msg
        ));
      });

      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to send file');
      }

      // Update message status to sent
      setMessages(prev => prev.map(msg => 
        msg.id === fileMessage.id 
          ? { ...msg, uploadStatus: 'sent', uploadProgress: 1 } 
          : msg
      ));

      console.log('✅ File sent successfully:', result.fileInfo);
    } catch (error) {
      console.error('❌ Failed to send file:', error);

      // Update message status to failed
      setMessages(prev => prev.map(msg => 
        msg.id === fileMessage.id 
          ? { ...msg, uploadStatus: 'failed' } 
          : msg
      ));

      // Show error message
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: 'عذراً، فشل إرسال الملف. يرجى المحاولة مرة أخرى.',
        sender: 'system',
        timestamp: new Date().toISOString(),
      }]);
    }
  };

  return (
    <div className={`chat-window ${isWidget ? 'chat-window-widget' : 'chat-window-app'}`} dir="rtl">
      <div className="chat-header">
        <div className="chat-header-info">
          <div className="chat-header-logo">
            <img src="/assets/header-logo.svg" alt="لبيه ميوا" className="header-logo-icon" />
          </div>
        </div>
        <div className="chat-header-actions">
          <button 
            className="chat-new-session-button" 
            onClick={handleNewSession}
            aria-label="بدء محادثة جديدة"
            title="بدء محادثة جديدة"
          >
            <img src="/assets/refresh.svg" alt="تحديث" className="refresh-icon" />
          </button>
          {isWidget && (
            <button 
              className="chat-close-button" 
              onClick={() => {
                console.log('🔴 Close button clicked!');
                console.log('🔴 onClose function:', onClose);
                if (onClose && typeof onClose === 'function') {
                  onClose();
                } else {
                  console.error('❌ onClose is not a function!', onClose);
                }
              }}
              aria-label="إغلاق المحادثة"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      
      <MessageList 
        messages={messages} 
        isLoading={isLoading} 
        isConnecting={isConnecting}
        agentState={agentState}
        onClickableTextClick={handleSendMessage}
      />
      
      <MessageInput 
        onSendMessage={handleSendMessage}
        onSendFile={handleSendFile}
        disabled={isLoading || isConnecting}
        showFileAttachment={humanAgentPresent}
      />
    </div>
  );
};

export default ChatWindow;
